import React, { useEffect, useState, useCallback } from "react";
import { Search, Filter, ChevronRight, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { AdminOrderDetail } from "./AdminOrderDetail";

const TABS = [
    { id: "all", label: "All", filters: {} },
    { id: "unfulfilled", label: "Unfulfilled", filters: { status: "unfulfilled" } },
    { id: "unpaid", label: "Unpaid", filters: { payment: "unpaid" } },
    { id: "paid", label: "Paid", filters: { payment: "paid" } },
    { id: "fulfilled", label: "Fulfilled", filters: { status: "fulfilled" } },
    { id: "canceled", label: "Canceled", filters: { status: "canceled" } },
];

const FULFILLMENT_PILL = {
    order_placed: { label: "Order Placed", cls: "bg-amber-500/10 border-amber-500/30 text-amber-200" },
    processing: { label: "Processing", cls: "bg-blue-500/10 border-blue-500/30 text-blue-200" },
    shipped: { label: "Shipped", cls: "bg-[rgba(233,188,11,0.1)] border-[rgba(233,188,11,0.35)] text-[var(--gold)]" },
    out_for_delivery: { label: "Out for Delivery", cls: "bg-orange-500/10 border-orange-500/30 text-orange-200" },
    delivered: { label: "Delivered", cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" },
    canceled: { label: "Canceled", cls: "bg-red-500/10 border-red-500/30 text-red-200" },
    unfulfilled: { label: "Unfulfilled", cls: "bg-white/5 border-white/10 text-white/60" },
};

const PAYMENT_PILL = {
    paid: { label: "Paid", cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" },
    awaiting_payment: { label: "Unpaid", cls: "bg-amber-500/10 border-amber-500/30 text-amber-200" },
    initiated: { label: "Pending", cls: "bg-amber-500/10 border-amber-500/30 text-amber-200" },
    pending: { label: "Pending", cls: "bg-amber-500/10 border-amber-500/30 text-amber-200" },
    canceled: { label: "Canceled", cls: "bg-red-500/10 border-red-500/30 text-red-200" },
    failed: { label: "Failed", cls: "bg-red-500/10 border-red-500/30 text-red-200" },
};

const Pill = ({ map, value }) => {
    const meta = map[value];
    if (!meta) return null;
    return (
        <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.cls}`}>
            {meta.label}
        </span>
    );
};

const formatDateGroup = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const isSameDay = (a, b) => a.toDateString() === b.toDateString();
    if (isSameDay(d, today)) return "Today";
    if (isSameDay(d, yesterday)) return "Yesterday";
    const within7 = (today - d) / (1000 * 60 * 60 * 24) < 7;
    if (within7) return d.toLocaleDateString("en-AU", { weekday: "long" });
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "long" });
};

export const AdminOrdersLog = ({ token }) => {
    const [tab, setTab] = useState("all");
    const [search, setSearch] = useState("");
    const [searchDebounced, setSearchDebounced] = useState("");
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setSearchDebounced(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const tabDef = TABS.find((t) => t.id === tab);
            const params = { ...tabDef.filters };
            if (searchDebounced.trim()) params.search = searchDebounced.trim();
            const { data } = await api.get("/admin/orders", {
                params,
                headers: { "X-Admin-Token": token },
            });
            setOrders(data);
        } catch (err) {
            toast.error("Failed to load orders");
        }
        setLoading(false);
    }, [tab, searchDebounced, token]);

    useEffect(() => {
        load();
    }, [load]);

    // Group by date for the Shopify-style date headers
    const groups = orders.reduce((acc, o) => {
        const k = formatDateGroup(o.created_at);
        (acc[k] = acc[k] || []).push(o);
        return acc;
    }, {});

    if (selected) {
        return (
            <AdminOrderDetail
                reference={selected}
                token={token}
                onBack={() => { setSelected(null); load(); }}
            />
        );
    }

    return (
        <div data-testid="admin-orders-log">
            {/* Search + refresh */}
            <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                        type="text"
                        placeholder="Search by reference, email, or name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-9 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="admin-orders-search"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-white"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                <button
                    onClick={load}
                    className="px-3 rounded-full border border-white/10 text-[var(--text-secondary)] hover:border-white/30 inline-flex items-center"
                    data-testid="admin-orders-refresh"
                    aria-label="Refresh"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
            </div>

            {/* Tabs (filter pills) */}
            <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="admin-orders-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition-colors ${
                            tab === t.id
                                ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                        }`}
                        data-testid={`admin-orders-tab-${t.id}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Orders list */}
            {loading && orders.length === 0 ? (
                <div className="text-center py-12 text-[var(--text-secondary)]">Loading orders…</div>
            ) : orders.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center text-[var(--text-secondary)]" data-testid="admin-orders-empty">
                    <Filter size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
                    No orders match these filters.
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(groups).map(([groupName, items]) => (
                        <div key={groupName}>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2 px-1">
                                {groupName}
                            </div>
                            <div className="space-y-2">
                                {items.map((o, idx) => {
                                    const itemsCount = (o.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
                                    const time = new Date(o.created_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true });
                                    return (
                                        <button
                                            key={o.reference || `row-${idx}`}
                                            onClick={() => setSelected(o.reference)}
                                            className="w-full text-left glass-elevated rounded-xl p-4 hover:border-[var(--gold)]/40 transition-colors"
                                            data-testid={`admin-order-row-${o.reference}`}
                                        >
                                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                                <div className="flex-1 min-w-[200px]">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-mono text-white font-bold tracking-wider text-sm">
                                                            #{o.reference}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                                                        {o.customer_name || o.customer_email || "—"} · {itemsCount} item{itemsCount === 1 ? "" : "s"} · {time}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                                        <Pill map={FULFILLMENT_PILL} value={o.fulfillment_status || "unfulfilled"} />
                                                        <Pill map={PAYMENT_PILL} value={o.payment_status} />
                                                        {o.provider && (
                                                            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] px-2 py-0.5 rounded-full border border-white/5">
                                                                {o.provider === "payid" ? "PayID" : o.provider === "bank_transfer" ? "Bank" : o.provider === "paypal" ? "PayPal" : o.provider}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-display font-bold text-white">A${Number(o.amount || 0).toFixed(2)}</div>
                                                    <ChevronRight size={16} className="text-[var(--text-muted)] inline mt-1" />
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
